/**
 * Production Smart Document Checklist Component
 * Uses real broker document data - NO MOCK DATA
 * KOPS-174: Document Usage Context Enhancement
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Row,
  Col,
  Alert,
  Button,
  Badge,
  Accordion,
  ListGroup,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Modal
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faFileAlt,
  faCloudDownload,
  faRobot,
  faLightbulb,
  faClipboardCheck,
  faBrain,
  faProjectDiagram,
  faCog,
  faPlus,
  faListCheck,
  faEye,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
// import StageDocumentConfigurator from './StageDocumentConfigurator'; // TODO: Component needs to be created

interface SmartDocumentChecklistProps {
  propertyId?: string;
  transactionId?: string;
  brokerageId: string;
  onDocumentSelect?: (documentId: string) => void;
  transactionType?: string;
  transactionRole?: string;
  transactionStage?: string;
  propertyData?: any;
}

interface DocumentItem {
  id: string;
  name: string;
  type: string;
  reason?: string;
  timing?: string;
  template_id?: string;
  source?: string;
  priority?: string;
  conditions?: string[];
  transaction_stages?: string[];
  is_placeholder?: boolean;
  s3_url?: string;
}

interface WorkflowItem {
  id: string;
  name: string;
  type: string;
  confidence: number;
  documents: any[];
  usage_count: number;
  reasons: string[];
}

interface ChecklistData {
  required: DocumentItem[];
  conditional: DocumentItem[];
  recommended: DocumentItem[];
  broker_templates: any[];
}

const SmartDocumentChecklistProduction: React.FC<SmartDocumentChecklistProps> = ({
  propertyId,
  transactionId,
  brokerageId,
  onDocumentSelect,
  transactionType,
  transactionRole,
  transactionStage,
  propertyData
}) => {
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState<ChecklistData | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);
  const [showConfigurator, setShowConfigurator] = useState(false);
  const [selectedTransactionType, setSelectedTransactionType] = useState<'buyer' | 'seller' | 'rental'>('buyer');
  const [brokerDocuments, setBrokerDocuments] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'all' | 'stages'>('all');
  const [propertyInfo, setPropertyInfo] = useState({
    type: 'residential',
    year_built: 2000,
    has_hoa: false,
    has_pool: false,
    has_solar: false,
    solar_ownership: 'owned',
    septic_system: false,
    water_source: 'city',
    flood_zone: 'X',
    requires_flood_insurance: false
  });
  // Start with no transaction type selected to prevent automatic API calls
  const [transactionInfo, setTransactionInfo] = useState<any>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch checklist data from production API
  useEffect(() => {
    console.log('📍 [SmartDocumentChecklist] Component mounted with:', {
      transactionId,
      brokerageId,
      transactionInfo,
      propertyInfo
    });
    fetchBrokerChecklist();
    fetchBrokerDocuments();
  }, [brokerageId, propertyInfo, transactionInfo]);

  const fetchBrokerChecklist = async () => {
    // Don't fetch if no transaction info is set
    if (!transactionInfo) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      // Use the new simplified endpoint for templates
      const response = await axiosInstance.get(`/broker-checklist/${brokerageId}/documents`, {
        params: {
          transaction_type: transactionInfo.type || 'purchase'
        }
      });

      if (response.data.success) {
        // Transform the simplified response to match the checklist structure
        // All documents go to "required" for templates
        const simplifiedChecklist = {
          required: response.data.documents || [],
          conditional: [],
          recommended: [],
          broker_templates: response.data.documents || []
        };
        setChecklist(simplifiedChecklist);
        setWorkflows([]);
      } else {
        throw new Error(response.data.error || 'Failed to fetch checklist');
      }
    } catch (err: any) {
      console.error('Error fetching broker checklist:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrokerDocuments = async () => {
    try {
      const response = await axiosInstance.get(`/broker/documents/${brokerageId}`, {
        params: {
          document_type: 'all',
          status: 'completed' // Only get documents that have been AI analyzed
        }
      });

      if (response.data.success) {
        setBrokerDocuments(response.data.documents || []);
      }
    } catch (err: any) {
      console.error('Error fetching broker documents:', err);
      // Don't show error for documents, it's not critical
    }
  };

  const handlePropertyChange = (field: string, value: any) => {
    setPropertyInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleTransactionChange = (field: string, value: any) => {
    setTransactionInfo(prev => {
      const updated = { ...prev, [field]: value };
      
      // Reset role when transaction type changes
      if (field === 'type') {
        switch (value) {
          case 'purchase':
            updated.role = 'buyer_agent';
            break;
          case 'sale':
            updated.role = 'seller_agent';
            break;
          case 'rental':
            updated.role = 'landlord_agent';
            break;
          case 'land_contract':
            updated.role = 'seller_agent';
            break;
          case 'referral':
            updated.role = 'referring_agent';
            break;
        }
      }
      
      return updated;
    });
  };

  const handleUseTemplate = (templateId: string) => {
    console.log('🔵 [SmartChecklist] Use Template clicked:', templateId);
    if (onDocumentSelect) {
      onDocumentSelect(templateId);
    }
  };

  const getSourceIcon = (source?: string) => {
    switch (source) {
      case 'broker_template':
        return faProjectDiagram;
      case 'broker_analysis':
      case 'relationship_analysis':
        return faRobot;
      default:
        return faFileAlt;
    }
  };

  const getSourceTooltip = (source?: string) => {
    switch (source) {
      case 'broker_template':
        return 'From your brokerage templates';
      case 'broker_analysis':
        return 'AI-discovered from your documents';
      case 'relationship_analysis':
        return 'Recommended based on document relationships';
      default:
        return 'Document requirement';
    }
  };

  const getSourceColor = (source?: string) => {
    switch (source) {
      case 'broker_template':
        return '#0d6efd';
      case 'broker_analysis':
      case 'relationship_analysis':
        return '#6f42c1';
      default:
        return '#6c757d';
    }
  };

  if (loading) {
    return (
      <Card style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" style={{ color: '#00d4ff' }} />
          <p className="mt-3 text-muted">Loading smart checklist...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
        <Card.Body>
          <Alert variant="danger">
            {error}
            <Button 
              variant="link" 
              size="sm" 
              onClick={fetchBrokerChecklist}
              className="float-end"
            >
              Retry
            </Button>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const totalRequired = (checklist?.required.length || 0) + (checklist?.conditional.length || 0);

  // Show initial selection interface if no transaction type selected
  if (!transactionInfo) {
    return (
      <Card style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
        <Card.Body>
          <div className="text-center py-5">
            <FontAwesomeIcon icon={faLightbulb} size="3x" className="mb-3" style={{ color: '#00d4ff' }} />
            <h5 className="mb-3">Browse Document Templates</h5>
            <p className="text-muted mb-4">
              Select a transaction type to view available document templates for that scenario
            </p>
            
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <Button
                variant="outline-primary"
                onClick={() => setTransactionInfo({
                  type: 'purchase',
                  role: 'buyer_agent',
                  loan_type: 'conventional',
                  financing_type: 'mortgage',
                  offer_price: 500000,
                  down_payment_percent: 20,
                  is_referral: false,
                  referral_fee_percent: 25,
                  current_stage: 'pre_contract'
                })}
              >
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                Purchase Documents
              </Button>
              
              <Button
                variant="outline-primary"
                onClick={() => setTransactionInfo({
                  type: 'sale',
                  role: 'seller_agent',
                  loan_type: 'conventional',
                  financing_type: 'mortgage',
                  offer_price: 500000,
                  down_payment_percent: 20,
                  is_referral: false,
                  referral_fee_percent: 25,
                  current_stage: 'pre_contract'
                })}
              >
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                Sale/Listing Documents
              </Button>
              
              <Button
                variant="outline-primary"
                onClick={() => setTransactionInfo({
                  type: 'rental',
                  role: 'landlord_agent',
                  loan_type: 'month_to_month',
                  financing_type: 'none',
                  offer_price: 2000,
                  down_payment_percent: 0,
                  is_referral: false,
                  referral_fee_percent: 0,
                  current_stage: 'pre_contract'
                })}
              >
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                Rental/Lease Documents
              </Button>
              
              <Button
                variant="outline-primary"
                onClick={() => setTransactionInfo({
                  type: 'land_contract',
                  role: 'seller_agent',
                  loan_type: 'land_contract',
                  financing_type: 'seller_financing',
                  offer_price: 100000,
                  down_payment_percent: 10,
                  is_referral: false,
                  referral_fee_percent: 0,
                  current_stage: 'pre_contract'
                })}
              >
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                Land Contract Documents
              </Button>
              
              <Button
                variant="outline-primary"
                onClick={() => setTransactionInfo({
                  type: 'referral',
                  role: 'referring_agent',
                  loan_type: 'conventional',
                  financing_type: 'mortgage',
                  offer_price: 0,
                  down_payment_percent: 0,
                  is_referral: true,
                  referral_fee_percent: 25,
                  current_stage: 'pre_contract'
                })}
              >
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                Referral Documents
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div>
      {/* Property & Transaction Info Form */}
      <Card className="mb-4" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
        <Card.Body>
          <h6 className="mb-3">
            <FontAwesomeIcon icon={faClipboardCheck} className="me-2" style={{ color: '#00d4ff' }} />
            Transaction Details
          </h6>
          
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label size="sm">Transaction Type</Form.Label>
                <Form.Select
                  size="sm"
                  value={transactionInfo.type}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleTransactionChange('type', e.target.value)}
                  style={{ borderColor: '#00d4ff' }}
                >
                  <option value="purchase">Purchase</option>
                  <option value="sale">Sale/Listing</option>
                  <option value="rental">Rental/Lease</option>
                  <option value="land_contract">Land Contract</option>
                  <option value="referral">Referral Only</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label size="sm">Your Role</Form.Label>
                <Form.Select
                  size="sm"
                  value={transactionInfo.role}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleTransactionChange('role', e.target.value)}
                  style={{ borderColor: '#00d4ff' }}
                >
                  {transactionInfo.type === 'purchase' && (
                    <>
                      <option value="buyer_agent">Buyer's Agent</option>
                      <option value="dual_agent">Dual Agent</option>
                    </>
                  )}
                  {transactionInfo.type === 'sale' && (
                    <>
                      <option value="seller_agent">Seller's Agent</option>
                      <option value="dual_agent">Dual Agent</option>
                    </>
                  )}
                  {transactionInfo.type === 'rental' && (
                    <>
                      <option value="landlord_agent">Landlord's Agent</option>
                      <option value="tenant_agent">Tenant's Agent</option>
                      <option value="dual_agent">Dual Agent</option>
                    </>
                  )}
                  {transactionInfo.type === 'land_contract' && (
                    <>
                      <option value="seller_agent">Seller's Agent</option>
                      <option value="buyer_agent">Buyer's Agent</option>
                    </>
                  )}
                  {transactionInfo.type === 'referral' && (
                    <>
                      <option value="referring_agent">Referring Agent</option>
                      <option value="receiving_agent">Receiving Agent</option>
                    </>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label size="sm">Property Type</Form.Label>
                <Form.Select
                  size="sm"
                  value={propertyInfo.type}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handlePropertyChange('type', e.target.value)}
                >
                  <option value="residential">Residential</option>
                  <option value="single_family">Single Family</option>
                  <option value="condo">Condo</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="multi_family">Multi-Family</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label size="sm">Year Built</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  value={propertyInfo.year_built}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    handlePropertyChange('year_built', parseInt(e.target.value))}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label size="sm">{transactionInfo.type === 'rental' ? 'Lease Term' : 'Loan Type'}</Form.Label>
                {transactionInfo.type === 'rental' ? (
                  <Form.Select
                    size="sm"
                    value={transactionInfo.loan_type}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                      handleTransactionChange('loan_type', e.target.value)}
                  >
                    <option value="month_to_month">Month-to-Month</option>
                    <option value="6_months">6 Months</option>
                    <option value="12_months">12 Months</option>
                    <option value="24_months">24 Months</option>
                  </Form.Select>
                ) : (
                  <Form.Select
                    size="sm"
                    value={transactionInfo.loan_type}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                      handleTransactionChange('loan_type', e.target.value)}
                  >
                    <option value="conventional">Conventional</option>
                    <option value="FHA">FHA</option>
                    <option value="VA">VA</option>
                    <option value="USDA">USDA</option>
                    <option value="cash">Cash</option>
                  </Form.Select>
                )}
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label size="sm">Has HOA?</Form.Label>
                <Form.Select
                  size="sm"
                  value={propertyInfo.has_hoa ? 'true' : 'false'}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                    handlePropertyChange('has_hoa', e.target.value === 'true')}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Referral Agreement Section */}
          {transactionInfo.type === 'referral' && (
            <div className="mt-3 p-3" style={{ 
              background: 'rgba(0, 212, 255, 0.1)', 
              borderRadius: '8px',
              border: '1px solid rgba(0, 212, 255, 0.3)'
            }}>
              <h6 className="mb-3">
                <FontAwesomeIcon icon={faFileAlt} className="me-2" style={{ color: '#00d4ff' }} />
                Referral Agreement Details
              </h6>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label size="sm">Referral Fee %</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      value={transactionInfo.referral_fee_percent}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                        handleTransactionChange('referral_fee_percent', parseInt(e.target.value))}
                      min="0"
                      max="100"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label size="sm">Commission Split</Form.Label>
                    <Form.Select
                      size="sm"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                        handleTransactionChange('commission_split', e.target.value)}
                    >
                      <option value="from_gross">From Gross Commission</option>
                      <option value="from_net">From Net Commission</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label size="sm">Referral Type</Form.Label>
                    <Form.Select
                      size="sm"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                        handleTransactionChange('referral_type', e.target.value)}
                    >
                      <option value="within_brokerage">Within Brokerage</option>
                      <option value="outside_brokerage">Outside Brokerage</option>
                      <option value="relocation">Relocation Company</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Smart Checklist */}
      <Card style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faBrain} className="me-2" style={{ color: '#00d4ff' }} />
              Smart Document Checklist
            </h5>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant={viewMode === 'stages' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setViewMode(viewMode === 'stages' ? 'all' : 'stages')}
                title="View by transaction stages"
              >
                <FontAwesomeIcon icon={faListCheck} className="me-2" />
                {viewMode === 'stages' ? 'All Docs' : 'By Stage'}
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowConfigurator(true)}
                title="Configure Stage Documents"
              >
                <FontAwesomeIcon icon={faCog} className="me-2" />
                Configure Templates
              </Button>
              <Badge 
                bg="primary" 
                style={{ fontSize: '14px', padding: '8px 12px' }}
              >
                {totalRequired} Documents Required
                {checklist?.broker_templates.length ? (
                  <Badge bg="secondary" className="ms-2">
                    {checklist.broker_templates.length} Templates
                  </Badge>
                ) : null}
              </Badge>
            </div>
          </div>

          {viewMode === 'all' ? (
            <Accordion defaultActiveKey="0">
            {/* Required Documents */}
            {checklist?.required && checklist.required.length > 0 && (
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-danger" />
                  Required Documents ({checklist.required.length})
                </Accordion.Header>
                <Accordion.Body>
                  <ListGroup variant="flush">
                    {checklist.required.map((doc) => (
                      <ListGroup.Item 
                        key={doc.id} 
                        className="d-flex justify-content-between align-items-start"
                        style={{ background: 'transparent', borderColor: 'rgba(255, 255, 255, 0.1)' }}
                      >
                        <div className="ms-2 me-auto">
                          <div className="fw-bold">
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>{getSourceTooltip(doc.source)}</Tooltip>}
                            >
                              <FontAwesomeIcon 
                                icon={getSourceIcon(doc.source)} 
                                className="me-2"
                                style={{ color: getSourceColor(doc.source) }}
                              />
                            </OverlayTrigger>
                            {doc.name}
                          </div>
                          {doc.reason && (
                            <small className="text-muted d-block">{doc.reason}</small>
                          )}
                          {doc.timing && (
                            <small className="text-info d-block">
                              <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                              Timing: {doc.timing}
                            </small>
                          )}
                        </div>
                        {doc.template_id && (
                          <Button 
                            variant={doc.is_placeholder ? "outline-warning" : "outline-primary"}
                            size="sm"
                            onClick={() => doc.is_placeholder ? console.log('Upload required') : handleUseTemplate(doc.template_id!)}
                          >
                            <FontAwesomeIcon icon={doc.is_placeholder ? faUpload : faCloudDownload} className="me-1" />
                            {doc.is_placeholder ? 'Upload Required' : 'Use Template'}
                          </Button>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Accordion.Body>
              </Accordion.Item>
            )}

            {/* Conditional Documents */}
            {checklist?.conditional && checklist.conditional.length > 0 && (
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
                  Conditional Documents ({checklist.conditional.length})
                </Accordion.Header>
                <Accordion.Body>
                  <ListGroup variant="flush">
                    {checklist.conditional.map((doc) => (
                      <ListGroup.Item 
                        key={doc.id}
                        style={{ background: 'transparent', borderColor: 'rgba(255, 255, 255, 0.1)' }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="ms-2 me-auto">
                            <div className="fw-bold">
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{getSourceTooltip(doc.source)}</Tooltip>}
                              >
                                <FontAwesomeIcon 
                                  icon={getSourceIcon(doc.source)} 
                                  className="me-2"
                                  style={{ color: getSourceColor(doc.source) }}
                                />
                              </OverlayTrigger>
                              {doc.name}
                            </div>
                            {doc.conditions && doc.conditions.map((cond, idx) => (
                              <small key={idx} className="text-warning d-block">
                                • {cond}
                              </small>
                            ))}
                          </div>
                          {doc.template_id && (
                            <Button 
                              variant={doc.is_placeholder ? "outline-warning" : "outline-warning"}
                              size="sm"
                              onClick={() => doc.is_placeholder ? console.log('Upload required') : handleUseTemplate(doc.template_id!)}
                            >
                              <FontAwesomeIcon icon={doc.is_placeholder ? faUpload : faCloudDownload} className="me-1" />
                              {doc.is_placeholder ? 'Upload' : 'Template'}
                            </Button>
                          )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Accordion.Body>
              </Accordion.Item>
            )}

            {/* Recommended Documents */}
            {checklist?.recommended && checklist.recommended.length > 0 && (
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
                  Recommended Documents ({checklist.recommended.length})
                </Accordion.Header>
                <Accordion.Body>
                  <ListGroup variant="flush">
                    {checklist.recommended.map((doc) => (
                      <ListGroup.Item 
                        key={doc.id}
                        style={{ background: 'transparent', borderColor: 'rgba(255, 255, 255, 0.1)' }}
                      >
                        <div className="fw-bold">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{getSourceTooltip(doc.source)}</Tooltip>}
                          >
                            <FontAwesomeIcon 
                              icon={getSourceIcon(doc.source)} 
                              className="me-2"
                              style={{ color: getSourceColor(doc.source) }}
                            />
                          </OverlayTrigger>
                          {doc.name}
                        </div>
                        {doc.reason && (
                          <small className="text-muted">{doc.reason}</small>
                        )}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Accordion.Body>
              </Accordion.Item>
            )}
          </Accordion>
          ) : (
            /* Stage-based View */
            <div>
              {['pre_contract', 'showing', 'under_contract', 'closing'].map(stage => {
                const stageLabel = stage.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
                
                // Filter documents for this stage
                const stageRequiredDocs = checklist?.required.filter(doc => 
                  doc.transaction_stages?.includes(stage) || 
                  (!doc.transaction_stages?.length && stage === 'pre_contract') // Default to pre-contract if no stages specified
                ) || [];
                
                const stageConditionalDocs = checklist?.conditional.filter(doc =>
                  doc.transaction_stages?.includes(stage)
                ) || [];

                const stageRecommendedDocs = checklist?.recommended?.filter((doc: any) =>
                  doc.transaction_stages?.includes(stage)
                ) || [];

                const totalStageDocs = stageRequiredDocs.length + stageConditionalDocs.length + stageRecommendedDocs.length;
                
                return (
                  <Accordion key={stage} className="mb-3">
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <div className="d-flex align-items-center w-100">
                          <FontAwesomeIcon 
                            icon={
                              stage === 'pre_contract' ? faFileAlt :
                              stage === 'showing' ? faEye :
                              stage === 'under_contract' ? faClipboardCheck :
                              faCheckCircle
                            } 
                            className="me-3" 
                            style={{ 
                              color: stage === transactionInfo.current_stage ? '#00d4ff' : '#6c757d',
                              fontSize: '1.2em'
                            }}
                          />
                          <div className="flex-grow-1">
                            <span className="fw-bold">{stageLabel}</span>
                            {stage === transactionInfo.current_stage && (
                              <Badge bg="info" className="ms-2">Current Stage</Badge>
                            )}
                          </div>
                          <Badge 
                            bg={totalStageDocs > 0 ? 'primary' : 'secondary'} 
                            className="ms-auto me-2"
                          >
                            {totalStageDocs} {totalStageDocs === 1 ? 'Document' : 'Documents'}
                          </Badge>
                          {(() => {
                            console.log('🔍 Button check:', {
                              stage,
                              currentStage: transactionInfo.current_stage,
                              isCurrentStage: stage === transactionInfo.current_stage,
                              totalStageDocs,
                              transactionId,
                              showButton: stage === transactionInfo.current_stage && totalStageDocs > 0 && transactionId
                            });
                            // Temporarily show button for all stages with documents
                            return totalStageDocs > 0 && transactionId && (
                              <Button
                                variant="primary"
                                size="sm"
                                className="ms-2"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                try {
                                  console.log(`📋 Copying all ${stage} documents to transaction`);
                                  
                                  // Collect all documents for this stage
                                  const stageDocs: Array<{templateId: string; templateName: string; reason: string; timing: string; s3_url?: string}> = [];

                                  // Add required documents
                                  stageRequiredDocs.forEach((doc: any) => {
                                    stageDocs.push({
                                      templateId: doc.id,
                                      templateName: doc.name,
                                      reason: doc.reason || `Required document for ${stageLabel}`,
                                      timing: doc.timing || stage,
                                      s3_url: doc.s3_url
                                    });
                                  });
                                  
                                  // Add conditional documents
                                  stageConditionalDocs.forEach((doc: any) => {
                                    stageDocs.push({
                                      templateId: doc.id,
                                      templateName: doc.name,
                                      reason: doc.reason || `Conditional document for ${stageLabel}`,
                                      timing: doc.timing || stage,
                                      s3_url: doc.s3_url
                                    });
                                  });
                                  
                                  // Add recommended documents
                                  stageRecommendedDocs.forEach((doc: any) => {
                                    stageDocs.push({
                                      templateId: doc.id,
                                      templateName: doc.name,
                                      reason: doc.reason || `Recommended document for ${stageLabel}`,
                                      timing: doc.timing || stage,
                                      s3_url: doc.s3_url
                                    });
                                  });
                                  
                                  console.log(`📋 Inheriting ${stageDocs.length} documents for stage: ${stage}`);
                                  
                                  // Use the existing inherit-documents endpoint
                                  const response = await axiosInstance.post(`/transactions/${transactionId}/inherit-documents`, {
                                    documents: stageDocs
                                  });
                                  
                                  if (response.data.success) {
                                    const results = response.data.results || [];
                                    const successful = results.filter((r: any) => r.success).length;
                                    const failed = results.filter((r: any) => !r.success).length;
                                    
                                    if (successful > 0) {
                                      alert(`Successfully added ${successful} documents to transaction${failed > 0 ? `. ${failed} documents were skipped.` : '.'}`);
                                    } else {
                                      alert('No documents were added. They may already exist in the transaction.');
                                    }
                                    
                                    // Notify parent to refresh documents if callback provided
                                    if (onDocumentSelect) {
                                      onDocumentSelect('refresh');
                                    }
                                  }
                                } catch (error) {
                                  console.error('Error copying stage documents:', error);
                                  alert('Failed to copy documents. Please try again.');
                                }
                              }}
                              title={`Add all ${stageLabel} documents to transaction`}
                            >
                              <FontAwesomeIcon icon={faPlus} className="me-1" />
                              Add to Transaction
                            </Button>
                          );
                          })()}
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        {/* Required Documents for this stage */}
                        {stageRequiredDocs.length > 0 && (
                          <div className="mb-3">
                            <h6 className="text-danger mb-2">
                              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                              Required Documents ({stageRequiredDocs.length})
                            </h6>
                            <ListGroup variant="flush">
                              {stageRequiredDocs.map((doc) => (
                                <ListGroup.Item 
                                  key={doc.id}
                                  className="d-flex justify-content-between align-items-start"
                                  style={{ background: 'transparent', borderColor: 'rgba(255, 255, 255, 0.1)' }}
                                >
                                  <div className="ms-2 me-auto">
                                    <div className="fw-bold">{doc.name}</div>
                                    {doc.reason && (
                                      <small className="text-muted d-block">{doc.reason}</small>
                                    )}
                                    {doc.timing && (
                                      <small className="text-info d-block">
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                        Timing: {doc.timing}
                                      </small>
                                    )}
                                  </div>
                                  {doc.template_id && (
                                    <Button 
                                      variant={doc.is_placeholder ? "outline-warning" : "outline-primary"}
                                      size="sm"
                                      onClick={() => doc.is_placeholder ? console.log('Upload required') : handleUseTemplate(doc.template_id!)}
                                    >
                                      <FontAwesomeIcon icon={doc.is_placeholder ? faUpload : faCloudDownload} className="me-1" />
                                      {doc.is_placeholder ? 'Upload' : 'Use'}
                                    </Button>
                                  )}
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          </div>
                        )}
                        
                        {/* Conditional Documents for this stage */}
                        {stageConditionalDocs.length > 0 && (
                          <div>
                            <h6 className="text-warning mb-2">
                              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                              Conditional Documents ({stageConditionalDocs.length})
                            </h6>
                            <ListGroup variant="flush">
                              {stageConditionalDocs.map((doc) => (
                                <ListGroup.Item 
                                  key={doc.id}
                                  style={{ background: 'transparent', borderColor: 'rgba(255, 255, 255, 0.1)' }}
                                >
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="ms-2 me-auto">
                                      <div className="fw-bold">{doc.name}</div>
                                      {doc.conditions && doc.conditions.map((cond, idx) => (
                                        <small key={idx} className="text-warning d-block">
                                          • {cond}
                                        </small>
                                      ))}
                                    </div>
                                    {doc.template_id && (
                                      <Button 
                                        variant="outline-warning" 
                                        size="sm"
                                        onClick={() => doc.is_placeholder ? console.log('Upload required') : handleUseTemplate(doc.template_id!)}
                                      >
                                        <FontAwesomeIcon icon={doc.is_placeholder ? faUpload : faCloudDownload} className="me-1" />
                                        {doc.is_placeholder ? 'Upload' : 'Use'}
                                      </Button>
                                    )}
                                  </div>
                                </ListGroup.Item>
                              ))}
                            </ListGroup>
                          </div>
                        )}
                        
                        {/* No documents message */}
                        {totalStageDocs === 0 && (
                          <p className="text-muted text-center my-3">
                            No documents required for this stage
                          </p>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                );
              })}
            </div>
          )}

          {/* Applicable Workflows */}
          {workflows.length > 0 && (
            <div className="mt-4">
              <h6 className="mb-3">
                <FontAwesomeIcon icon={faProjectDiagram} className="me-2" style={{ color: '#00d4ff' }} />
                Suggested Workflows
              </h6>
              <ListGroup variant="flush">
                {workflows.map((workflow) => (
                  <ListGroup.Item 
                    key={workflow.id}
                    style={{ background: 'rgba(255, 255, 255, 0.03)', borderColor: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{workflow.name}</strong>
                        <div className="small text-muted">
                          Confidence: {workflow.confidence}% • Used {workflow.usage_count} times
                        </div>
                        <div className="small text-info">
                          {workflow.reasons.join(' • ')}
                        </div>
                      </div>
                      <Badge bg="info">{workflow.documents.length} docs</Badge>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          {/* No Data Message */}
          {(!checklist || (checklist.required.length === 0 && 
             checklist.conditional.length === 0 && 
             checklist.recommended.length === 0)) && (
            <Alert variant="info" className="mt-3">
              <FontAwesomeIcon icon={faLightbulb} className="me-2" />
              No document patterns found yet. Upload brokerage documents to start building your smart checklist!
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Stage Document Configurator Modal */}
      <Modal
        show={showConfigurator}
        onHide={() => setShowConfigurator(false)}
        size="xl"
        fullscreen="lg-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCog} className="me-2" />
            Configure Document Templates
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Form.Group>
              <Form.Label>Select Transaction Type</Form.Label>
              <Form.Select
                value={selectedTransactionType}
                onChange={(e) => setSelectedTransactionType(e.target.value as 'buyer' | 'seller' | 'rental')}
                className="mb-3"
              >
                <option value="buyer">Buyer Transaction</option>
                <option value="seller">Seller Transaction</option>
                <option value="rental">Rental/Lease Transaction</option>
              </Form.Select>
            </Form.Group>
          </div>

          {/* <StageDocumentConfigurator
            brokerageId={brokerageId}
            transactionType={selectedTransactionType}
            existingDocuments={brokerDocuments}
            onSave={(template) => {
              console.log('Template saved:', template);
              setShowConfigurator(false);
              // Refresh checklist to show new template
              fetchBrokerChecklist();
              fetchBrokerDocuments();
            }}
            onCancel={() => setShowConfigurator(false)} /> */}
          <div className="alert alert-warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            Stage Document Configurator is temporarily unavailable
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SmartDocumentChecklistProduction;